/**
 * Google Tag (gtag.js) wrapper for Google Ads conversion tracking + Analytics.
 *
 * Loaded programmatically (no inline script in index.html) so our CSP stays
 * strict — no 'unsafe-inline' needed. Everything is a no-op when the tag ID
 * env var isn't set, so local dev is unaffected.
 *
 * Env vars (set in .env.local AND in Vercel project settings):
 *   VITE_GTAG_ID                       e.g. AW-1234567890  (Google Ads conversion ID)
 *   VITE_GTAG_CONV_SIGNUP              e.g. AW-1234567890/AbCdEfGhIj  (per-conversion label)
 *   VITE_GTAG_CONV_FIRST_EXTRACTION    (same format)
 *   VITE_GTAG_CONV_SUBSCRIPTION
 *   VITE_GTAG_CONV_CREDIT_PURCHASE
 *
 * The conversion labels each come from a different Conversion Action you create
 * in Google Ads (Tools -> Conversions -> + New conversion -> Website).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const GTAG_ID = import.meta.env.VITE_GTAG_ID;

let initialized = false;

export function initGtag() {
  if (!GTAG_ID) {
    if (import.meta.env.PROD) {
      console.info("[gtag] VITE_GTAG_ID not set — tag disabled");
    }
    return;
  }
  if (initialized || typeof window.gtag === "function") return;

  // Bootstrap dataLayer BEFORE the script loads so events queued early aren't lost
  window.dataLayer = window.dataLayer || [];
  // Use function declaration (not arrow) so `arguments` works the way gtag expects
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Send the initial bootstrap events
  window.gtag("js", new Date());
  window.gtag("config", GTAG_ID, {
    send_page_view: true,
    // Anonymize IP — modest privacy improvement, EU-friendly
    anonymize_ip: true,
  });

  // Inject the gtag.js loader
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
  document.head.appendChild(script);

  initialized = true;
}

/** Fire a custom event (analytics, not a Google Ads conversion). */
export function trackEvent(name: string, params?: Record<string, unknown>) {
  if (!GTAG_ID || !window.gtag) return;
  window.gtag("event", name, params ?? {});
}

/**
 * Fire a Google Ads conversion. `sendTo` is the full conversion label
 * (`AW-1234567890/AbCdEfGhIj`) — typically a `VITE_GTAG_CONV_*` env var.
 * Returns silently if the env var is missing.
 */
export function trackConversion(sendTo: string | undefined, value?: number, currency = "USD") {
  if (!GTAG_ID || !window.gtag || !sendTo) return;
  const payload: Record<string, unknown> = { send_to: sendTo, currency };
  if (typeof value === "number") payload.value = value;
  window.gtag("event", "conversion", payload);
}

/** Call this on every client-side route change so SPA navigations are counted. */
export function trackPageView(path: string) {
  if (!GTAG_ID || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
  });
}

export function isGtagEnabled() {
  return Boolean(GTAG_ID);
}
